import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import {
    getUserLicenses,
    type License,
} from "@/lib/license-service";
import { products as allProducts, type Product } from "@/data/products";
import {
    Package,
    Key,
    Copy,
    Check,
    Monitor,
    Loader2,
    Download,
    BookOpen,
    ShoppingCart,
    Sparkles,
    Plus,
    ExternalLink
} from "lucide-react";

// Mapping from products.tsx (frontend) to supabase products (by name)
// We'll match by name or display all products

interface ProgramItem {
    product: Product;
    license: License | null;
    isPurchased: boolean;
}

const AllProgramsList = () => {
    const { t } = useLanguage();
    const { toast } = useToast();
    const { user } = useAuth();
    const [licenses, setLicenses] = useState<License[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [copiedKey, setCopiedKey] = useState<string | null>(null);

    useEffect(() => {
        if (user) {
            fetchLicenses();
        } else {
            setIsLoading(false);
        }
    }, [user]);

    const fetchLicenses = async () => {
        if (!user) return;
        setIsLoading(true);
        try {
            const data = await getUserLicenses(user.id);
            setLicenses(data);
        } catch (error) {
            console.error("Error fetching licenses:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopyKey = async (licenseKey: string) => {
        try {
            await navigator.clipboard.writeText(licenseKey);
            setCopiedKey(licenseKey);
            toast({
                title: t("คัดลอกแล้ว!", "Copied!"),
                description: t("คัดลอก License Key ไปยัง Clipboard", "License key copied to clipboard")
            });
            setTimeout(() => setCopiedKey(null), 2000);
        } catch {
            toast({
                variant: "destructive",
                title: t("เกิดข้อผิดพลาด", "Error"),
                description: t("ไม่สามารถคัดลอกได้", "Could not copy to clipboard")
            });
        }
    };

    // Build a merged list of all products with their purchase status
    const programItems: ProgramItem[] = allProducts.map((product) => {
        // Try to find a license that matches this product by name
        const matchingLicense = licenses.find(
            (lic) => lic.products?.name?.toLowerCase() === product.name.toLowerCase()
        );

        return {
            product,
            license: matchingLicense || null,
            isPurchased: !!matchingLicense
        };
    });

    if (isLoading) {
        return (
            <Card variant="glass">
                <CardContent className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </CardContent>
            </Card>
        );
    }

    return (
        <Card variant="glass">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Package className="h-5 w-5" />
                    {t("โปรแกรมทั้งหมด", "All Programs")}
                    <Badge variant="secondary" className="ml-2">{programItems.length}</Badge>
                </CardTitle>
                <CardDescription>
                    {t("โปรแกรม AI สำหรับ Creator พร้อมใช้งานทันที", "AI programs for Creators, ready to use")}
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {programItems.map((item) => {
                    const { product, license, isPurchased } = item;

                    return (
                        <div
                            key={product.id}
                            className={`p-4 rounded-xl border transition-all ${isPurchased
                                    ? "bg-green-500/5 border-green-500/20 hover:border-green-500/40"
                                    : "bg-muted/30 border-border/50 hover:border-primary/40"
                                }`}
                        >
                            {/* Header Row */}
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isPurchased ? "bg-green-500/10" : "bg-primary/10"
                                        }`}>
                                        <div className={isPurchased ? "text-green-500" : "text-primary"}>
                                            {product.icon}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-semibold">{product.name}</h3>
                                            {isPurchased && (
                                                <Badge className="bg-green-500 text-white text-[10px]">
                                                    {t("ซื้อแล้ว", "Purchased")}
                                                </Badge>
                                            )}
                                        </div>
                                        <p className="text-xs text-muted-foreground line-clamp-1">
                                            {t(product.descriptionTh, product.descriptionEn)}
                                        </p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    {!isPurchased && (
                                        <span className="text-lg font-bold text-gradient">
                                            ฿{product.price.toLocaleString()}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* License Key Row (only if purchased) */}
                            {isPurchased && license && (
                                <div className="mb-3 p-3 bg-background/50 rounded-lg border border-border/50">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                            <Key className="h-4 w-4 text-green-500 flex-shrink-0" />
                                            <code className="font-mono text-sm truncate">
                                                {license.license_key}
                                            </code>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 flex-shrink-0"
                                            onClick={() => handleCopyKey(license.license_key)}
                                        >
                                            {copiedKey === license.license_key ? (
                                                <Check className="h-4 w-4 text-green-500" />
                                            ) : (
                                                <Copy className="h-4 w-4" />
                                            )}
                                        </Button>
                                    </div>
                                    <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                                        <Monitor className="h-3 w-3" />
                                        <span>
                                            {t("เครื่องที่ใช้", "Machines")}: {license.current_activations}/{license.max_activations}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex items-center justify-end gap-2 flex-wrap">
                                {isPurchased && license ? (
                                    // Purchased: Show 3 buttons - License, Download, Manual
                                    <>
                                        <Button variant="outline" size="sm" className="gap-1.5" asChild>
                                            <Link to="/my-programs">
                                                <Plus className="h-3.5 w-3.5" />
                                                {t("สร้าง License", "Create License")}
                                            </Link>
                                        </Button>
                                        {license.products?.download_url && (
                                            <Button variant="outline" size="sm" className="gap-1.5" asChild>
                                                <a href={license.products.download_url} target="_blank" rel="noopener noreferrer">
                                                    <Download className="h-3.5 w-3.5" />
                                                    {t("ดาวน์โหลด", "Download")}
                                                </a>
                                            </Button>
                                        )}
                                        <Button variant="outline" size="sm" className="gap-1.5" asChild>
                                            <a href={`/ai-program/${product.id}`} target="_blank" rel="noopener noreferrer">
                                                <BookOpen className="h-3.5 w-3.5" />
                                                {t("คู่มือการใช้งาน", "User Manual")}
                                            </a>
                                        </Button>
                                    </>
                                ) : (
                                    // Not purchased: Show buy button
                                    <Button variant="glow" size="sm" className="gap-1.5" asChild>
                                        <Link to={`/ai-program/${product.id}`}>
                                            <ShoppingCart className="h-3.5 w-3.5" />
                                            {t("สั่งซื้อ", "Order Now")}
                                        </Link>
                                    </Button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </CardContent>
        </Card>
    );
};

export default AllProgramsList;
